<script
  setup
  lang="ts"
  generic="T extends string, Item extends { value: T; label: string } = { value: T; label: string }"
>
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
} from 'reka-ui'
import { computed, nextTick, onUnmounted, ref, useSlots, useTemplateRef } from 'vue'

const props = defineProps<{
  modelValue: T | readonly T[] | undefined
  options: readonly Item[]
  triggerLabel: string
  searchLabel: string
  emptyText: string
  ariaLabel?: string
  /** Selecting toggles membership in an array `modelValue` instead of replacing a single value. */
  multiple?: boolean
  /** The trigger takes its container's full width instead of sizing to its label (a sidebar). */
  fill?: boolean
  /** `chip` drops the trigger's own border and ground, for a caller that draws a filter chip around it. */
  variant?: 'field' | 'chip'
}>()

const emit = defineEmits<{ 'update:modelValue': [value: T | readonly T[]] }>()

// No data-[state=open] border: unlike Select.vue, this popover's width is independent
// of the trigger's own (min 288px, see updatePopoverPosition), so a highlighted trigger
// border reads as a second, misaligned box stacked over the real panel below it.
const FIELD_TRIGGER_CLASS =
  'h-[var(--density-control)] gap-[var(--space-inline)] border border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)] pl-[var(--space-cell)] text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)] outline-none transition-colors duration-[var(--motion-fast)] hover:border-[color:var(--color-border-control)]'
const CHIP_TRIGGER_CLASS =
  'h-full gap-[var(--space-tight)] pl-[var(--space-cell)] text-[length:var(--text-step-2)]'

const selectedValues = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []))
const currentLabel = computed(() => {
  if (props.multiple) {
    const count = selectedValues.value.length
    return count === 0 ? props.triggerLabel : `${props.triggerLabel} (${count})`
  }
  return props.options.find((option) => option.value === props.modelValue)?.label ?? props.triggerLabel
})

// role="listbox" (ComboboxContent) may only contain role="option" children, so the search
// input can't live inside it - and reka's dismiss-on-outside-focus treats anything outside
// both Content's own DOM and the root's own (unportalled) wrapper as "outside", so the
// input can't be portalled either or focusing it closes the popover immediately. Only
// Content - genuinely a floating list that needs to escape a scrolling ancestor - portals;
// the input stays a real sibling, positioned by hand like AccountCombobox.vue's own popover.
const triggerRef = useTemplateRef<{ $el: HTMLElement }>('trigger')
const inputRef = useTemplateRef<{ $el: HTMLElement }>('input')
const inputStyle = ref<{ top: string; left: string; width: string }>({
  top: '0px',
  left: '0px',
  width: '0px',
})
const listStyle = ref<{ top: string; left: string; width: string }>({ top: '0px', left: '0px', width: '0px' })
const contentRef = useTemplateRef<{ $el: HTMLElement }>('content')
const footerStyle = ref<{ top: string; left: string; width: string }>({
  top: '0px',
  left: '0px',
  width: '0px',
})
const hasFooter = 'footer' in useSlots()
let resizeObserver: ResizeObserver | undefined

function updateFooterPosition(): void {
  const rect = contentRef.value?.$el.getBoundingClientRect()
  if (rect === undefined) return
  footerStyle.value = { top: `${rect.bottom}px`, left: `${rect.left}px`, width: `${rect.width}px` }
}

function updatePopoverPosition(): void {
  const rect = triggerRef.value?.$el.getBoundingClientRect()
  if (rect === undefined) return
  const width = `${Math.max(rect.width, 288)}px`
  inputStyle.value = { top: `${rect.bottom + 4}px`, left: `${rect.left}px`, width }
  const inputRect = inputRef.value?.$el.getBoundingClientRect()
  const listTop = inputRect === undefined ? rect.bottom + 4 + 44 : inputRect.bottom
  listStyle.value = { top: `${listTop}px`, left: `${rect.left}px`, width }
}

const isOpen = ref(false)

function close(): void {
  isOpen.value = false
  onOpenChange(false)
}

function onOpenChange(next: boolean): void {
  isOpen.value = next
  if (next) {
    updatePopoverPosition()
    void nextTick(updatePopoverPosition)
    if (hasFooter) {
      void nextTick(() => {
        updateFooterPosition()
        const el = contentRef.value?.$el
        if (el !== undefined && typeof ResizeObserver !== 'undefined') {
          resizeObserver?.disconnect()
          resizeObserver = new ResizeObserver(updateFooterPosition)
          resizeObserver.observe(el)
        }
      })
    }
    return
  }
  resizeObserver?.disconnect()
  // Content's own focus-restore-on-unmount only fires when the input was a DOM
  // descendant of it; ours is a sibling by necessity (see comment above), so this
  // component owns returning focus to the trigger once the popover closes - after
  // the input itself unmounts, or its own blur would just hand focus to <body>.
  void nextTick(() => triggerRef.value?.$el.focus())
}

function onReposition(): void {
  updatePopoverPosition()
  if (hasFooter) void nextTick(updateFooterPosition)
}
window.addEventListener('scroll', onReposition, true)
window.addEventListener('resize', onReposition)
onUnmounted(() => {
  resizeObserver?.disconnect()
  window.removeEventListener('scroll', onReposition, true)
  window.removeEventListener('resize', onReposition)
})
</script>

<template>
  <ComboboxRoot
    v-slot="{ open }"
    :multiple="multiple ?? false"
    :open="isOpen"
    v-bind="modelValue === undefined ? {} : { modelValue: modelValue as T | T[] }"
    @update:model-value="(value: T | T[]) => emit('update:modelValue', value)"
    @update:open="onOpenChange"
  >
    <ComboboxAnchor as-child>
      <ComboboxTrigger
        ref="trigger"
        :aria-label="ariaLabel ?? currentLabel"
        :class="[fill ? 'w-full' : 'max-w-64', variant === 'chip' ? CHIP_TRIGGER_CLASS : FIELD_TRIGGER_CLASS]"
        class="flex items-center truncate rounded-[var(--radius-control)] pr-[var(--space-inline)]"
      >
        <slot name="trigger" :label="currentLabel">
          <span class="truncate">{{ currentLabel }}</span>
        </slot>
        <slot name="indicator">
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            aria-hidden="true"
            class="shrink-0 text-[color:var(--color-text-muted)]"
          >
            <path
              d="M4 6.5 8 10.5 12 6.5"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </slot>
      </ComboboxTrigger>
    </ComboboxAnchor>
    <ComboboxInput
      v-if="open"
      ref="input"
      auto-focus
      :display-value="() => ''"
      :aria-label="searchLabel"
      :placeholder="searchLabel"
      class="fixed z-50 rounded-t-[var(--radius-overlay)] border border-b-0 border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)] shadow-[var(--shadow-overlay)] placeholder:text-[color:var(--color-text-muted)]"
      :style="{ ...inputStyle, height: 'var(--density-control)' }"
    />
    <ComboboxPortal>
      <ComboboxContent
        v-if="open"
        ref="content"
        position="inline"
        class="fixed z-50 flex flex-col overflow-hidden border border-[color:var(--color-border)] bg-[color:var(--color-surface)] shadow-[var(--shadow-overlay)]"
        :class="hasFooter ? undefined : 'rounded-b-[var(--radius-overlay)]'"
        :style="listStyle"
      >
        <ComboboxViewport class="flex max-h-64 flex-col overflow-auto p-[var(--space-tight)]">
          <ComboboxItem
            v-for="option in options"
            :key="option.value"
            :value="option.value"
            :text-value="option.label"
            class="flex min-h-[var(--density-target-min)] w-full cursor-pointer select-none items-center gap-[var(--space-inline)] rounded-[var(--radius-control)] px-[var(--space-cell)] py-[var(--space-inline)] text-left outline-none data-[highlighted]:bg-[color:var(--color-surface-raised)]"
            :class="
              (multiple ? selectedValues.includes(option.value) : option.value === modelValue)
                ? 'shadow-[inset_2px_0_0_0_var(--color-accent)]'
                : undefined
            "
          >
            <slot name="item" :option="option">
              <span
                class="min-w-0 flex-1 truncate text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
              >
                {{ option.label }}
              </span>
            </slot>
          </ComboboxItem>
          <ComboboxEmpty
            class="px-[var(--space-cell)] py-[var(--space-group)] text-center text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
          >
            {{ emptyText }}
          </ComboboxEmpty>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxPortal>
    <div
      v-if="open && hasFooter"
      class="fixed z-50 rounded-b-[var(--radius-overlay)] border border-t-0 border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-tight)] shadow-[var(--shadow-overlay)]"
      :style="footerStyle"
    >
      <slot name="footer" :close="close" />
    </div>
  </ComboboxRoot>
</template>
